import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as Yup from "yup";
import clsx from "clsx";
import { useFormik } from "formik";
import { setUser } from "../redux/AuthSlice";
import { useApolloClient } from "@apollo/client";
import { SIGNUP } from "src/setup/apollo/mutations";

const signupSchema = Yup.object().shape({
  username: Yup.string()
    .min(3, "Minimum 3 karakter")
    .max(50, "Maximum 50 karakter")
    .matches(/^[a-zA-Z0-9]+$/, "Kullanıcı adı yalnızca harf ve sayı içerebilir")
    .required("Kullanıcı adı gereklidir"),
  password: Yup.string()
    .min(8, "Minimum 8 karakter")
    .max(50, "Maximum 50 karakter")
    .matches(/[a-z]/, "Şifre en az bir küçük harf içermelidir")
    .matches(/[A-Z]/, "Şifre en az bir büyük harf içermelidir")
    .matches(/[\W_]/, "Şifre en az bir özel karakter içermelidir")
    .required("Şifre gereklidir"),
  passwordRepeat: Yup.string()
    .oneOf([Yup.ref("password")], "Şifreler eşleşmiyor")
    .required("Şifre tekrarı gereklidir"),
  phoneNumber: Yup.string(),
  salesPersonName: Yup.string()
    .required("Ad Soyad gereklidir"),
  token: Yup.string()
    .required("Token gereklidir"),
});

export function SignUp() {
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const client = useApolloClient();

  // Get token from URL and handle + characters that might be converted to spaces
  const getTokenFromUrl = () => {
    // Get the raw search string
    const rawSearch = window.location.search;
    const tokenMatch = rawSearch.match(/[?&]token=([^&]*)/);
    if (tokenMatch) {
      let tokenValue = tokenMatch[1];
      
      // Try to decode URI component (handles %2B, %2F, %3D, etc.)
      try {
        tokenValue = decodeURIComponent(tokenValue);
      } catch (e) {
        // If decoding fails, use as-is
      }
      
      // Browsers convert + to spaces in query params before JavaScript sees them
      // Base64 tokens contain + but never spaces, so replace spaces with +
      // This is safe because base64 only contains A-Z, a-z, 0-9, +, /, =
      tokenValue = tokenValue.replace(/ /g, '+');
      
      return tokenValue;
    }
    return "";
  };

  const token = getTokenFromUrl();

  // Helper function to get meaningful error message from error code
  const getErrorMessage = (errorCode, errorMessage) => {
    if (!errorCode) {
      // If no error code, use the error message (first 40 chars) + meaningful text
      const shortMessage = errorMessage ? errorMessage.substring(0, 40) : "";
      return shortMessage ? `${shortMessage}... Lütfen tekrar deneyiniz.` : "Kayıt işlemi başarısız. Lütfen tekrar deneyiniz.";
    }

    switch (errorCode) {
      case "USER_ALREADY_EXISTS":
        return errorCode + " - Bu kullanıcı adı zaten kullanılıyor. Lütfen farklı bir kullanıcı adı seçiniz.";
      case "DECRYPTION_FAILED":
        return errorCode + " - Token geçersiz. Lütfen e-postanızdaki bağlantıyı kullanınız. Sorun devam ederse yöneticiye başvurunuz.";
      case "INVALID_SUBSCRIPTION_KEY":
        return errorCode + " - Abonelik anahtarı geçersiz. Lütfen yöneticiye başvurunuz.";
      case "INVALID_SUBSCRIPTION_FORMAT":
        return errorCode + " - Token geçersiz. Lütfen e-postanızdaki bağlantıyı kullanınız. Eğer bağlantıyı kullandıysanız yöneticiye başvurunuz.";
      case "LC_401_ERROR":
        return errorCode + " - Lütfen yöneticiye başvurunuz.";
      case "USER_CREATION_FAILED":
        return errorCode + " - Kullanıcı oluşturulamadı. Lütfen tekrar deneyiniz.";
      case "ROLE_ASSIGNMENT_FAILED":
        return errorCode + " - Yapılandırma hatası. Lütfen yöneticiden yardım isteyiniz.";
      default:
        // For any other error, show first 40 chars of error code/message + meaningful text
        const shortError = errorCode.substring(0, 40);
        return `${shortError}... Lütfen tekrar deneyiniz.`;
    }
  };

  useEffect(() => {
    if (!token) {
      // If no token, redirect to login
      navigate("/auth");
    }
  }, [token, navigate]);

  const formik = useFormik({
    initialValues: {
      username: "",
      password: "",
      passwordRepeat: "",
      phoneNumber: "",
      salesPersonName: "",
      token: token,
    },
    validationSchema: signupSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setLoading(true);
      try {
        // Use token from URL params to ensure it's always current
        // Get token using the same method that handles + characters correctly
        const currentToken = getTokenFromUrl() || values.token;
        if (!currentToken) {
          setStatus("Token bulunamadı. Lütfen e-postanızdaki bağlantıyı kullanınız.");
          setLoading(false);
          setSubmitting(false);
          return;
        }

        const response = await client.mutate({
          mutation: SIGNUP,
          variables: {
            model: {
              username: values.username,
              password: values.password,
              phoneNumber: values.phoneNumber || null,
              salesPersonName: values.salesPersonName,
              token: currentToken,
            },
          },
        });

        // Check for GraphQL errors first
        if (response?.errors && response.errors.length > 0) {
          const error = response.errors[0];
          const errorCode = error?.extensions?.code || error?.extensions?.exceptionCode || error?.code;
          const errorMessage = error?.message || error?.extensions?.exceptionMessage || "";
          setStatus(getErrorMessage(errorCode, errorMessage));
          setLoading(false);
          setSubmitting(false);
          return;
        }

        if (response?.data?.signup) {
          const data = response.data.signup;
          
          // Check if signup response has error status or exceptionMessage
          if (data.status && data.status !== "SUCCESS") {
            const errorCode = data.status;
            const errorMessage = data.exceptionMessage || "";
            setStatus(getErrorMessage(errorCode, errorMessage));
            setLoading(false);
            setSubmitting(false);
            return;
          }
          
          if (data.response && data.userInfo) {
            // Login successful - same flow as Login component
            dispatch(
              setUser({
                salesPersonId: data.userInfo.salesPersonId,
                salesPersonGuid: data.userInfo.salesPersonGuid,
                salesPersonName: data.userInfo.salesPersonName,
                token: data.response,
                serverUrl: "",
                userData: data.userInfo,
              })
            );
            // Reload to trigger authentication check
            document.location.reload(); 
          } else {
            // No userInfo means signup failed
            const errorCode = data.status || data.exceptionCode || data.response || "";
            const errorMessage = data.exceptionMessage || data.response || "";
            setStatus(getErrorMessage(errorCode, errorMessage));
            setLoading(false);
            setSubmitting(false);
          }
        } else {
          const errorCode = data.status || data.exceptionCode || data.response || "";
          const errorMessage = data.exceptionMessage || data.response || "";
          setStatus(getErrorMessage(errorCode, errorMessage));
          setLoading(false);
          setSubmitting(false);
        }
      } catch (error) {
        setLoading(false);
        setSubmitting(false);
        
        // Try to extract error code from GraphQL error
        const errorCode = error?.graphQLErrors?.[0]?.extensions?.code || 
                         error?.graphQLErrors?.[0]?.extensions?.exceptionCode ||
                         error?.graphQLErrors?.[0]?.code ||
                         error?.extensions?.code ||
                         error?.code;
        const errorMessage = error?.graphQLErrors?.[0]?.message ||
                            error?.graphQLErrors?.[0]?.extensions?.exceptionMessage ||
                            error?.message ||
                            "";
        
        setStatus(getErrorMessage(errorCode, errorMessage));
      }
    },
  });

  return (
    <form
      className="form"
      onSubmit={formik.handleSubmit}
      noValidate
      id="signup_form"
    >
      {/* begin::Heading */}
      <div className="text-center mb-5 d-flex items-center justify-content-center flex-column">
        <h3 className="text-dark mb-3">SABAK</h3>
        <h3 className="text-dark mb-3">Hesap Oluştur</h3>
      </div>
      {/* begin::Heading */}

      {formik.status ? (
        <div className="mb-lg-15 alert alert-danger">
          <div className="alert-text font-weight-bold">{formik.status}</div>
        </div>
      ) : (
        <span></span>
      )}
      
      {/* begin::Form group - Sales Person Name */}
      <div className="h-100 mb-10 mt-4">
        <label className="form-label fs-6 fw-bolder text-dark">Ad Soyad</label>
        <input
          placeholder="Ad Soyad"
          {...formik.getFieldProps("salesPersonName")}
          className={clsx(
            "form-control form-control-lg form-control-solid",
            { "is-invalid": formik.touched.salesPersonName && formik.errors.salesPersonName },
            {
              "is-valid": formik.touched.salesPersonName && !formik.errors.salesPersonName,
            }
          )}
          type="text"
          name="salesPersonName"
          autoComplete="name"
        />
        {formik.touched.salesPersonName && formik.errors.salesPersonName && (
          <div className="fv-plugins-message-container">
            <span role="alert">{formik.errors.salesPersonName}</span>
          </div>
        )}
      </div>

      {/* begin::Form group - Username */}
    <div className="h-100 mt-2">
        <label className="form-label fs-6 fw-bolder text-dark">Kullanıcı Adı</label>
        <input
          placeholder="Kullanıcı Adı"
          {...formik.getFieldProps("username")}
          className={clsx(
            "form-control form-control-lg form-control-solid",
            { "is-invalid": formik.touched.username && formik.errors.username },
            {
              "is-valid": formik.touched.username && !formik.errors.username,
            }
          )}
          type="text"
          name="username"
          autoComplete="username"
        />
        {formik.touched.username && formik.errors.username && (
          <div className="fv-plugins-message-container">
            <span role="alert">{formik.errors.username}</span>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Form group - Password */}
      <div className="h-100 mt-2">
        <label className="form-label fs-6 fw-bolder text-dark">Şifre</label>
        <input
          type="password"
          placeholder="*******"
          autoComplete="new-password"
          {...formik.getFieldProps("password")}
          className={clsx(
            "form-control form-control-lg form-control-solid",
            {
              "is-invalid": formik.touched.password && formik.errors.password,
            },
            {
              "is-valid": formik.touched.password && !formik.errors.password,
            }
          )}
        />
        {formik.touched.password && formik.errors.password && (
          <div className="fv-plugins-message-container">
            <span role="alert">{formik.errors.password}</span>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Form group - Password Repeat */}
      <div className="h-100 mt-2">
        <label className="form-label fs-6 fw-bolder text-dark">Şifre Tekrar</label>
        <input
          type="password"
          placeholder="*******"
          autoComplete="new-password"
          {...formik.getFieldProps("passwordRepeat")}
          className={clsx(
            "form-control form-control-lg form-control-solid",
            {
              "is-invalid": formik.touched.passwordRepeat && formik.errors.passwordRepeat,
            },
            {
              "is-valid": formik.touched.passwordRepeat && !formik.errors.passwordRepeat,
            }
          )}
        />
        {formik.touched.passwordRepeat && formik.errors.passwordRepeat && (
          <div className="fv-plugins-message-container">
            <span role="alert">{formik.errors.passwordRepeat}</span>
          </div>
        )}
      </div>
      {/* end::Form group - Password Repeat */}

      {/* begin::Form group - Phone Number */}
      <div className="h-100 mt-2">
        <label className="form-label fs-6 fw-bolder text-dark">
          Telefon Numarası <span className="text-muted">(Opsiyonel)</span>
        </label>
        <input
          placeholder="Telefon Numarası"
          {...formik.getFieldProps("phoneNumber")}
          className={clsx(
            "form-control form-control-lg form-control-solid",
            { "is-invalid": formik.touched.phoneNumber && formik.errors.phoneNumber },
            {
              "is-valid": formik.touched.phoneNumber && !formik.errors.phoneNumber,
            }
          )}
          type="tel"
          name="phoneNumber"
          autoComplete="tel"
        />
        {formik.touched.phoneNumber && formik.errors.phoneNumber && (
          <div className="fv-plugins-message-container">
            <span role="alert">{formik.errors.phoneNumber}</span>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* end::Form group */}

      {/* begin::Action */}
      <div className="text-center">
        <button
          type="submit"
          id="signup_submit"
          className="btn btn-lg btn-primary w-100 mt-4 mb-3"
          disabled={formik.isSubmitting || !formik.isValid}
        >
          {!loading && <span className="indicator-label">KAYIT OL</span>}
          {loading && (
            <span className="indicator-progress" style={{ display: "block" }}>
              Kayıt yapılıyor...
              <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
            </span>
          )}
        </button>
        <p className="text-center mb-0">
          <span className="text-muted">Zaten hesabınız var mı? </span>
          <a
            href="/auth"
            className="text-primary text-decoration-none fw-bold"
          >
            Giriş yap
          </a>
        </p>
      </div>
      {/* end::Action */}
    </form>
  );
}

